import { useEffect } from 'react';

import { parseAudioShareLink } from '../../common/audioSharingUtils';
import type { ActiveContent } from '../../types/activeContent';

import { useAudioPlayerStore } from './useAudioPlayerStore';

/**
 * Hook that detects and handles audio share links from URL parameters on initial page load.
 * When an ?audioShare parameter is present, it parses the link, updates the current
 * activeContent with the supporting content from the share link, sets the active feature,
 * and opens the audio modal.
 *
 * This hook runs only once on component mount when the page is initially loaded with
 * the audioShare query parameter.
 *
 * @param activeContent - The active content to open if audio sharing link was detected.
 * @param isUninitialized - Whether or not the audio player is uninitialized.
 * @param isDisabled - Whether or not the hook is disabled (activeContent is not ready or audio is disabled).
 */
export const useAudioShareLink = (activeContent: ActiveContent, isUninitialized: boolean, isDisabled?: boolean) => {
	const { setActiveFeature, openAudioPlayer } = useAudioPlayerStore();

	useEffect(() => {
		if (isDisabled) {
			return;
		}

		const audioShareParam = new URLSearchParams(window.location.search).get('audioShare');
		if (!audioShareParam) {
			return;
		}

		const { feature, supportingContent } = parseAudioShareLink(window.location.href);
		setActiveFeature(feature);
		openAudioPlayer({...activeContent, supportingContent}, { isUninitialized });

		const url = new URL(window.location.href);
		url.searchParams.delete('audioShare');
		window.history.replaceState({}, '', url.toString());
	}, [setActiveFeature, openAudioPlayer, activeContent, isDisabled, isUninitialized]);
};
